import { usePageMeta } from '../lib/meta';
import { CONTACT_EMAIL } from '../config';

const LAST_REVIEWED = 'July 18, 2026';

export function AccessibilityPage() {
  usePageMeta({
    title: 'Accessibility',
    description:
      'The accessibility commitment for PeacockOmlettes.com: our WCAG 2.1 AA target, the measures in place, known limitations, and how to report a barrier.',
    path: '/accessibility',
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-3xl text-plume-950 sm:text-5xl">Accessibility</h1>
      <p className="mt-3 text-sm text-plume-600">Last reviewed: {LAST_REVIEWED}</p>

      <div className="mt-8 space-y-8 leading-relaxed text-plume-900">
        <section>
          <h2 className="font-display text-2xl text-plume-950">Our commitment</h2>
          <p className="mt-3">
            PeacockOmlettes.com is meant to be usable by everyone, including people who rely on
            assistive technology. We aim for conformance with the{' '}
            <a
              href="https://www.w3.org/TR/WCAG21/"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-4"
            >
              Web Content Accessibility Guidelines (WCAG) 2.1
            </a>{' '}
            at Level AA across the site.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Measures in place</h2>
          <ul className="mt-3 list-disc space-y-2 pl-6">
            <li>Semantic HTML with named landmarks, one main heading per page, and a logical heading order</li>
            <li>A skip-to-content link, visible keyboard focus indicators, and focus management on page navigation</li>
            <li>Full keyboard operability: every feature, including search and consent controls, works without a mouse</li>
            <li>Search results announced to screen readers as you type</li>
            <li>Color contrast checked against the WCAG AA thresholds for text and interactive elements</li>
            <li>Decorative illustrations hidden from assistive technology; informative text is real text, not images</li>
            <li>Layouts that reflow for zoom up to 400% and small screens</li>
            <li>Animations and smooth scrolling disabled for visitors who prefer reduced motion</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Known limitations</h2>
          <p className="mt-3">
            Advertising served by our third-party ad partner (Google) renders inside its own
            frames, and its content is outside our direct control. The consent dialog is
            Google&rsquo;s certified consent platform, which Google maintains for accessibility.
            If any of this content gives you trouble, contact us and we will do what we can,
            including escalating to the vendor.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Report a barrier</h2>
          <p className="mt-3">
            If any part of this site is difficult or impossible for you to use, please email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold underline underline-offset-4">
              {CONTACT_EMAIL}
            </a>{' '}
            and describe the page and the problem. Accessibility reports go to the top of the
            queue; we aim to respond within five business days and to fix confirmed barriers
            promptly.
          </p>
        </section>
      </div>
    </div>
  );
}
